import { useEffect, useState } from "react";
import {
  AlertTriangleIcon,
  CheckIcon,
  CopyIcon,
  EyeIcon,
  EyeOffIcon,
  KeyRoundIcon,
  PlusIcon,
  ServerIcon,
  TerminalSquareIcon,
  Trash2Icon,
} from "lucide-react";

import {
  useCreateMcpToken,
  useMcpTokens,
  useRevokeMcpToken,
} from "../app/queries";
import { Topbar } from "../components/Topbar";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "../components/ui/alert";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "../components/ui/field";
import { Input } from "../components/ui/input";
import { Separator } from "../components/ui/separator";
import { Skeleton } from "../components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  Page,
  PageBody,
  PageDescription,
  PageHeader,
  PageTitle,
} from "../components/PageLayout";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { copyToClipboard } from "../lib/clipboard";
import { formatExact, relativeTime, shortDate } from "../lib/format";
import { cn } from "../lib/utils";
import type { McpTokenCreated, McpTokenSummary } from "../lib/api";

/**
 * SettingsMcpPage is the global admin page for MCP tokens. MCP tokens
 * are server-wide credentials: the URL itself is the secret. The page
 * mirrors the per-project API keys page in shape (mint → copy URL →
 * revoke) but lives outside the project layout because tokens are not
 * scoped to any project.
 */
export function SettingsMcpPage() {
  const tokens = useMcpTokens();

  const [mintDialogOpen, setMintDialogOpen] = useState(false);
  const [minted, setMinted] = useState<McpTokenCreated | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<McpTokenSummary | null>(null);
  const [revokeTargetName, setRevokeTargetName] = useState<string | null>(null);

  const create = useCreateMcpToken({
    success: (res) => `Minted MCP token ${res.tokenPrefix}`,
    error: (err) =>
      err instanceof Error ? err.message : "Failed to mint MCP token",
    onSuccess: (res) => {
      setMinted(res);
      setMintDialogOpen(false);
    },
  });
  const revoke = useRevokeMcpToken({
    success: () =>
      revokeTargetName ? `Revoked MCP token ${revokeTargetName}` : "MCP token revoked",
    error: (err) =>
      err instanceof Error ? err.message : "Failed to revoke MCP token",
    onSuccess: () => {
      setRevokeTarget(null);
      setRevokeTargetName(null);
    },
  });

  const items = tokens.data?.tokens ?? [];

  return (
    <Page>
      <Topbar
        breadcrumb={[
          { label: "Settings", to: "/settings/mcp" },
          { label: "MCP access" },
        ]}
      />
      <PageHeader>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <PageTitle>MCP access</PageTitle>
            <PageDescription>
              Issue MCP tokens that give agents the same view a human
              gets via the dashboard. The token is the URL — anyone who
              knows it has full admin-scope access.
            </PageDescription>
          </div>
          <Button
            size="sm"
            onClick={() => setMintDialogOpen(true)}
            disabled={create.isPending}
            data-testid="open-mint-mcp-dialog"
          >
            <PlusIcon /> Mint MCP token
          </Button>
        </div>
      </PageHeader>
      <PageBody width="wide">
        <div className="grid grid-cols-1 gap-8 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <div className="flex flex-col gap-8">
            {minted ? (
              <MintedToken
                minted={minted}
                acknowledged={acknowledged}
                onAcknowledge={setAcknowledged}
                onDismiss={() => {
                  setMinted(null);
                  setAcknowledged(false);
                }}
              />
            ) : null}

            <Card>
              <CardHeader>
                <CardTitle>Active MCP tokens</CardTitle>
                <CardDescription>
                  Each token is a global credential that authenticates
                  requests to{" "}
                  <span className="font-mono">/api/mcp/v1/{`<token>`}</span>{" "}
                  using a streamable HTTP transport. Store the URL
                  securely — Mushi can only show it once.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {tokens.isLoading ? (
                  <Skeleton className="h-10 w-full" />
                ) : tokens.isError ? (
                  <Alert variant="destructive">
                    <AlertDescription>{tokens.error.message}</AlertDescription>
                  </Alert>
                ) : items.length === 0 ? (
                  <div
                    data-testid="empty-mcp-tokens"
                    className="flex flex-col items-start gap-2 py-6 text-sm text-muted-foreground"
                  >
                    <p>
                      No MCP tokens yet. Mint one to let an agent connect
                      to this Mushi instance.
                    </p>
                  </div>
                ) : (
                  <McpTokenTable
                    tokens={items}
                    onRevoke={(t) => {
                      setRevokeTarget(t);
                      setRevokeTargetName(t.name);
                    }}
                  />
                )}
              </CardContent>
            </Card>
          </div>

          <McpExplainerCard />
        </div>
      </PageBody>

      <MintMcpDialog
        open={mintDialogOpen}
        busy={create.isPending}
        error={create.error?.message ?? null}
        onCancel={() => setMintDialogOpen(false)}
        onConfirm={async (name) => {
          try {
            await create.mutateAsync(name);
          } catch {
            // error toast handled by useCreateMcpToken
          }
        }}
      />

      <ConfirmDialog
        open={!!revokeTarget}
        title="Revoke MCP token"
        message={
          revokeTarget ? (
            <>
              Revoke the MCP token{" "}
              <strong>{revokeTarget.name}</strong>? Any agent currently
              using this URL will lose access immediately. This action
              cannot be undone.
            </>
          ) : (
            ""
          )
        }
        confirmLabel="Revoke token"
        destructive
        busy={revoke.isPending}
        onCancel={() => {
          setRevokeTarget(null);
          setRevokeTargetName(null);
        }}
        onConfirm={async () => {
          if (!revokeTarget) return;
          try {
            await revoke.mutateAsync(revokeTarget.id);
          } catch {
            // error toast handled by useRevokeMcpToken
          }
        }}
      />
    </Page>
  );
}

function MintMcpDialog({
  open,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (name: string) => void;
}) {
  const [name, setName] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setName("");
      setTouched(false);
    }
  }, [open]);

  const trimmed = name.trim();
  const tooLong = trimmed.length > 80;
  const empty = trimmed.length === 0;
  const valid = !empty && !tooLong;
  const showError = touched && (empty || tooLong);

  return (
    <Dialog
      open={open}
      onOpenChange={(next: boolean) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ServerIcon /> Mint MCP token
          </DialogTitle>
          <DialogDescription>
            Give the token a meaningful name (e.g.{" "}
            <span className="font-mono">claude-desktop</span> or{" "}
            <span className="font-mono">ci-triage-bot</span>). The URL is
            shown only once after creation — copy it before closing this
            dialog.
          </DialogDescription>
        </DialogHeader>
        <form
          data-testid="mint-mcp-dialog"
          onSubmit={(e) => {
            e.preventDefault();
            setTouched(true);
            if (!valid) return;
            onConfirm(trimmed);
          }}
        >
          <FieldGroup>
            <Field data-invalid={showError || undefined}>
              <FieldLabel htmlFor="mcp-mint-name">Token name</FieldLabel>
              <Input
                id="mcp-mint-name"
                required
                maxLength={80}
                autoFocus
                placeholder="e.g. claude-desktop"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => setTouched(true)}
                aria-invalid={showError || undefined}
                data-testid="mcp-mint-name"
                className="font-mono"
              />
              {showError ? (
                <FieldDescription
                  data-testid="mcp-mint-name-error"
                  className="text-destructive"
                >
                  {empty
                    ? "Token name is required."
                    : "Token name must be 80 characters or fewer."}
                </FieldDescription>
              ) : (
                <FieldDescription>
                  Used in the dashboard only. Up to 80 characters.
                </FieldDescription>
              )}
            </Field>
            {error ? (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={busy}
                data-testid="confirm-mcp-mint"
                onClick={() => setTouched(true)}
              >
                {busy ? "Minting…" : "Mint token"}
              </Button>
            </DialogFooter>
          </FieldGroup>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function MintedToken({
  minted,
  acknowledged,
  onAcknowledge,
  onDismiss,
}: {
  minted: McpTokenCreated;
  acknowledged: boolean;
  onAcknowledge: (acknowledged: boolean) => void;
  onDismiss: () => void;
}) {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);

  async function copyUrl() {
    const result = await copyToClipboard(minted.url);
    if (result.ok) {
      setCopied(true);
      setCopyError(null);
      window.setTimeout(() => setCopied(false), 1500);
    } else {
      setCopyError(result.error);
    }
  }

  return (
    <Card data-testid="minted-mcp-token">
      <CardContent className="flex flex-col gap-4">
        <Alert variant="destructive" data-testid="minted-mcp-banner">
          <AlertTriangleIcon aria-hidden="true" />
          <AlertTitle>Copy this URL now — it will not be shown again</AlertTitle>
          <AlertDescription>
            Anyone with this URL can read and modify every project on this
            server via the MCP protocol. Store it in a secret manager or
            environment variable — never commit it to source control.
          </AlertDescription>
        </Alert>

        <div className="flex flex-wrap items-stretch gap-2">
          <code
            data-testid="minted-mcp-url"
            className="relative block min-w-0 flex-1 break-all rounded-md border border-border bg-muted px-3 py-2 font-mono text-xs text-foreground"
          >
            {revealed ? minted.url : maskUrl(minted.url)}
          </code>
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setRevealed((v) => !v)}
                  aria-label={revealed ? "Hide URL" : "Reveal URL"}
                  data-testid="reveal-minted-mcp-url"
                />
              }
            >
              {revealed ? <EyeOffIcon /> : <EyeIcon />}
              {revealed ? "Hide" : "Reveal"}
            </TooltipTrigger>
            <TooltipContent>
              {revealed
                ? "Conceal the URL so the screen is safe to share."
                : "Show the full URL. Treat what you see as a secret."}
            </TooltipContent>
          </Tooltip>
          <Button
            type="button"
            size="sm"
            onClick={copyUrl}
            data-testid="copy-minted-mcp-url"
            className="shrink-0"
          >
            {copied ? (
              <>
                <CheckIcon /> Copied
              </>
            ) : (
              <>
                <CopyIcon /> Copy URL
              </>
            )}
          </Button>
        </div>

        {copyError ? (
          <p className="text-xs text-destructive" role="status">
            {copyError}
          </p>
        ) : null}

        <Separator />

        <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
          <span>
            Token prefix{" "}
            <span className="font-mono text-foreground">
              {minted.tokenPrefix}
            </span>{" "}
            appears in audit logs.
          </span>
          <div className="flex items-center gap-2">
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                className="size-4 rounded border-border accent-foreground"
                checked={acknowledged}
                onChange={(e) => onAcknowledge(e.target.checked)}
                data-testid="acknowledge-minted-mcp"
              />
              I have stored the URL
            </label>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={onDismiss}
              disabled={!acknowledged}
              className="text-muted-foreground"
            >
              Dismiss
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function McpTokenTable({
  tokens,
  onRevoke,
}: {
  tokens: McpTokenSummary[];
  onRevoke: (t: McpTokenSummary) => void;
}) {
  return (
    <ul data-testid="mcp-token-list" className="flex flex-col">
      {tokens.map((t, idx) => (
        <li
          key={t.id}
          data-testid="mcp-token-row"
          className={cn(
            "grid grid-cols-1 gap-4 px-4 py-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center",
            idx > 0 && "border-t border-border",
          )}
        >
          <div className="flex min-w-0 items-start gap-3">
            <span className="grid size-9 shrink-0 place-items-center rounded-md bg-muted text-muted-foreground">
              <KeyRoundIcon />
            </span>
            <div className="flex min-w-0 flex-col gap-1.5">
              <span className="truncate font-medium text-foreground">
                {t.name}
              </span>
              <code className="truncate font-mono text-xs text-muted-foreground">
                {t.tokenPrefix}…
              </code>
              <dl className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <dt>Created</dt>
                  <dd title={formatExact(t.createdAt)}>
                    {shortDate(t.createdAt)}
                  </dd>
                </div>
                <div className="flex items-center gap-1.5">
                  <dt>Last used</dt>
                  <dd>
                    {t.lastUsedAt ? (
                      <span title={formatExact(t.lastUsedAt)}>
                        {relativeTime(t.lastUsedAt)}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">Never used</span>
                    )}
                  </dd>
                </div>
                {t.revokedAt ? (
                  <div className="flex items-center gap-1.5">
                    <dt>Revoked</dt>
                    <dd title={formatExact(t.revokedAt)}>
                      {shortDate(t.revokedAt)}
                    </dd>
                  </div>
                ) : null}
              </dl>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:justify-end">
            <Badge
              variant="secondary"
              className={
                t.revokedAt
                  ? "text-muted-foreground"
                  : "border-success/30 bg-success/10 text-success"
              }
            >
              {t.revokedAt ? "revoked" : "active"}
            </Badge>
            {!t.revokedAt ? (
              <Button
                type="button"
                size="sm"
                variant="destructive"
                onClick={() => onRevoke(t)}
                data-testid="revoke-mcp-token"
              >
                <Trash2Icon /> Revoke
              </Button>
            ) : null}
          </div>
        </li>
      ))}
    </ul>
  );
}

function McpExplainerCard() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TerminalSquareIcon className="size-4 text-muted-foreground" />
          Connect an MCP client
        </CardTitle>
        <CardDescription>
          Paste the minted URL into any MCP client that supports the
          streamable HTTP transport. Most modern clients accept a URL
          instead of a stdio command, so no extra configuration is
          required.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <ul className="flex flex-col gap-2 text-sm text-muted-foreground">
          <li>
            <code className="font-mono text-foreground">list_projects</code>{" "}
            — discover every project on this server.
          </li>
          <li>
            <code className="font-mono text-foreground">list_reports</code>{" "}
            — search/filter reports across projects.
          </li>
          <li>
            <code className="font-mono text-foreground">get_report</code>{" "}
            — fetch a single report including attachments and activity.
          </li>
          <li>
            <code className="font-mono text-foreground">update_report</code>{" "}
            — change status, severity, title, description, or assignee.
          </li>
          <li>
            <code className="font-mono text-foreground">delete_report</code>{" "}
            — remove a report and its attachments.
          </li>
          <li>
            <code className="font-mono text-foreground">get_attachment</code>{" "}
            — fetch attachment metadata plus a download URL.
          </li>
        </ul>
        <p className="text-xs text-muted-foreground">
          Tokens are global: a single token can see every project, just
          like the admin token that authenticates this dashboard. Revoke
          a token to cut access immediately.
        </p>
      </CardContent>
    </Card>
  );
}

function maskUrl(url: string): string {
  // Keep host + protocol visible (so the user can confirm the origin)
  // but mask the token portion of the path.
  const idx = url.indexOf("/api/mcp/v1/");
  if (idx < 0) return "•".repeat(url.length);
  const head = url.slice(0, idx + "/api/mcp/v1/".length);
  const tail = url.slice(idx + "/api/mcp/v1/".length);
  if (tail.length <= 12) return head + "•".repeat(tail.length);
  return `${head}${tail.slice(0, 6)}${"•".repeat(Math.max(8, tail.length - 12))}${tail.slice(-6)}`;
}